/**
 * Take a board row — or one run's whole contribution to the board — off the board,
 * with the reason the user types.
 *
 * `leaderboard.board` is cumulative across every run and rebuilt from
 * `leaderboard.targets` on boot, so a sweep run to *test the harness* sits on it
 * beside real candidates, permanently. One bad run put 29 entries there: no
 * rationale, no pathway, a tier the ranker invented. This is the affordance that
 * says so.
 *
 * **Both granularities, in one place, on purpose.** The contaminated row is what a
 * reader notices — they open it and want the whole run gone, not 29 typed reasons.
 * The run offered here is the newest one that ranked *this* target, the same run
 * `BoardReport` files a report against and named for the same reason.
 *
 * The justification is **required**: confirm stays disabled until something is typed,
 * so the API's 422 is never the feedback. Six months on, this sentence is the only
 * thing distinguishing a discarded harness test from a hypothesis a scientist
 * rejected on the evidence, and the board's whole value is that the distinction
 * survives.
 *
 * Nothing here deletes and nothing here scores. The row and its reason stay, so a
 * later sweep proposing the same target cannot read as a first discovery, and no
 * score, tier, Elo or rank moves — a human judging a *run* must not be able to move
 * what the harness measured. The dialog says all three, because a button that hides
 * its consequences gets clicked by someone who thinks it is a delete.
 */
import { useState } from 'react'
import { Archive } from 'lucide-react'
import { ConfirmDialog } from './ConfirmDialog'
import { Button } from './ui/Button'

/** Which of the two actions the dialog is currently asking about. */
type Scope = { kind: 'target'; label: string } | { kind: 'run'; runId: string }

interface Props {
  targetKey: string
  protein: string
  /**
   * The newest run that ranked this target, or `null` when the observation log has
   * not loaded. The run action is hidden without it rather than guessed — retiring
   * the wrong run's rows is not something a refresh undoes.
   */
  runId: string | null
  /** `target_key` or `run_id` whose retirement is in flight. */
  retiring: string | null
  error: string | null
  onRetireTarget: (justification: string) => Promise<boolean>
  onRetireRun: (justification: string) => Promise<boolean>
}

export function RetireButton({
  targetKey,
  protein,
  runId,
  retiring,
  error,
  onRetireTarget,
  onRetireRun,
}: Props) {
  const [scope, setScope] = useState<Scope | null>(null)
  const [justification, setJustification] = useState('')

  const busy = retiring === targetKey || (runId !== null && retiring === runId)
  const reason = justification.trim()

  const close = () => {
    setScope(null)
    setJustification('')
  }

  const confirm = () => {
    if (!scope || !reason) return
    void (async () => {
      const done =
        scope.kind === 'target'
          ? await onRetireTarget(reason)
          : await onRetireRun(reason)
      // Only on success. A failed attempt keeps the dialog — and the typed reason —
      // so the user can retry without writing it again.
      if (done) close()
    })()
  }

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => setScope({ kind: 'target', label: protein })}
        >
          <Archive className="mr-1.5 h-3 w-3" />
          Retire this target
        </Button>
        {runId && (
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => setScope({ kind: 'run', runId })}
          >
            Retire everything from this run
          </Button>
        )}
      </div>

      {scope && (
        <ConfirmDialog
          title={
            scope.kind === 'target'
              ? `Retire ${scope.label} from the board?`
              : 'Retire every board row from this run?'
          }
          confirmLabel="Retire"
          busy={busy}
          confirmDisabled={reason.length === 0}
          error={error}
          onConfirm={confirm}
          onCancel={close}
        >
          {scope.kind === 'target' ? (
            <p>
              <span className="font-mono text-slate-200">{targetKey}</span> comes off
              the default board.
            </p>
          ) : (
            <p>
              Every board row{' '}
              <span className="font-mono text-slate-200">{scope.runId}</span>{' '}
              contributed comes off the default board — including rows another run
              also proposed, because the board row they share is what is being
              judged.
            </p>
          )}
          <p className="text-slate-400">
            Nothing is deleted and nothing is rescored. The row keeps its history and
            your reason, so a later run proposing the same target still shows as a
            repeat rather than as a new discovery, and no score, tier, Elo or rank
            changes.
          </p>
          <label className="block">
            <span className="text-2xs uppercase tracking-wide text-slate-400">
              Why (required)
            </span>
            <textarea
              // Takes first focus: `ConfirmDialog` focuses its confirm button, which
              // is disabled until something is typed here, and `focus()` on a
              // disabled button is a no-op — so this wins without either file
              // knowing about the other.
              autoFocus
              rows={3}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              maxLength={2000}
              placeholder="e.g. harness test sweep, ranker wrote no rationale"
              className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 p-2 font-sans text-sm text-slate-200 placeholder:text-slate-600 focus:border-brand-500 focus:outline-none"
            />
          </label>
        </ConfirmDialog>
      )}
    </>
  )
}
